import React, {useState, useCallback, useRef, useEffect} from 'react';
import './styles.css';
import ModelSelector from './components/ModelSelector';
import SettingsModal from './components/SettingsModal';
import {sendRequest} from './api/oneAPI';
import {DndProvider} from 'react-dnd';
import {HTML5Backend} from 'react-dnd-html5-backend';
import DraggableModelOutput from './components/DraggableModelOutput';
import HistorySidebar from './components/HistorySidebar';
import ChatModelSelector from './components/ChatModelSelector';
import ChatWindow from './components/ChatWindow';

import settingsIcon from './assets/icons/setting.svg';
import douIcon from './assets/icons/doubao.png';


const App = () => {
    const [selectedModels, setSelectedModels] = useState([]);
    const [outputs, setOutputs] = useState({});
    const [prompt, setPrompt] = useState('');
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const controllerRef = useRef(null);
    const [config, setConfig] = useState(null);  // 用于存储最终合并后的配置
    const [conversations, setConversations] = useState({}); // 改为对象格式,按模型分开存储对话
    const [activeConversationId, setActiveConversationId] = useState({});  // 每个模型独立的当前对话ID
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [isChatMode, setIsChatMode] = useState(false);
    // const [mode, setMode] = useState('parallel'); // 'parallel' 或 'dialog'
    

    // 将 moveModel 移到这里，和其他 hooks 放在一起
    const moveModel = useCallback((dragIndex, hoverIndex) => {
        setSelectedModels((prevModels) => {
            const newModels = [...prevModels];
            const [removed] = newModels.splice(dragIndex, 1);
            newModels.splice(hoverIndex, 0, removed);
            return newModels;
        });
    }, []);

    // 加载默认配置和用户配置
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                // 加载 config.json 作为默认配置
                const response = await fetch('/config.json');
                if (!response.ok) {
                    throw new Error('Failed to load configuration');
                }
                const data = await response.json();

                // 从 localStorage 获取用户自定义的配置
                const savedApiSources = JSON.parse(localStorage.getItem('apiSources')) || [];
                const savedModelConfigs = JSON.parse(localStorage.getItem('modelConfigs')) || [];

                // 合并默认配置和用户配置
                const mergedConfig = {
                    apiConfig: {
                        ...data.apiConfig, // 默认 API 配置
                        ...savedApiSources.reduce((acc, api) => ({...acc, [api.name]: api}), {}) // 合并用户 API 配置
                    },
                    models: [
                        ...data.models, // 默认模型配置
                        ...savedModelConfigs // 合并用户模型配置
                    ]
                };
                // console.log('合并后的 API 配置:', mergedConfig.apiConfig);
                // console.log('合并后的模型配置:', mergedConfig.models);
                setConfig(mergedConfig);  // 更新状态，触发重新渲染
            } catch (error) {
                console.error('Error loading configuration:', error);
            }
        };

        fetchConfig();
    }, []);

    // 初始化加载对话历史
    useEffect(() => {
        const savedConversations = JSON.parse(localStorage.getItem('conversations')) || {};
        setConversations(savedConversations);
        
        // 为每个模型设置活跃对话
        const activeIds = {};
        Object.entries(savedConversations).forEach(([modelValue, modelConvs]) => {
            if (modelConvs.length > 0) {
                activeIds[modelValue] = modelConvs[0].id;
            }
        });
        setActiveConversationId(activeIds);
    }, []);

    // 监听 selectedModels 变化来重置流状态
    useEffect(() => {
        // 切换模型时重置流状态
        setIsStreaming(false);
        if (controllerRef.current) {
            controllerRef.current.abort();
        }
    }, [selectedModels]);

    // 如果配置未加载，显示占位内容
    if (!config) {
        return <div>Loading configuration...</div>;
    }

    const {models, apiConfig} = config;

    const handleModelSelect = (modelValue) => {
        if (modelValue === 'All-In-One') {
            const allModelValues = models.map((m) => m.value);
            setSelectedModels((prev) =>
                prev.length === allModelValues.length ? [] : allModelValues
            );
        } else {
            setSelectedModels((prev) =>
                prev.includes(modelValue)
                    ? prev.filter((v) => v !== modelValue)
                    : [...prev, modelValue]
            );
        }
    };

    const handlePromptChange = (e) => setPrompt(e.target.value);

    const handleSelectConversation = (modelValue, conversationId) => {
        setActiveConversationId(prev => ({
            ...prev,
            [modelValue]: conversationId
        }));
    };

    const handleSubmit = async () => {
        if (!prompt.trim()) return;

        // 如果正在流式输出，则中断当前请求
        if (isStreaming) {
            if (controllerRef.current) {
                controllerRef.current.abort();
            }
            setIsStreaming(false);
            return;
        }

        // 在对话模式下检查是否选择了模型
        if (isChatMode && (!selectedModels.length || !selectedModels[0])) {
            alert('请先选择一个模型');
            return;
        }

        const timestamp = new Date().getTime();
        setPrompt('');
        setIsStreaming(true);

        try {
            if (isChatMode) {
                await handleDialogMode(selectedModels[0], prompt, timestamp);
            } else {
                await handleParallelMode(selectedModels, prompt, timestamp);
            }
        } catch (error) {
            console.error('Error in handleSubmit:', error);
        } finally {
            setIsStreaming(false);
        }
    };

    const handleDialogMode = async (modelValue, userPrompt, timestamp) => {
        const model = models.find((m) => m.value === modelValue);
        const {apiName} = model;
        const api = apiConfig[apiName];

        // 创建或获取当前对话
        let currentConversation;
        let currentMessages = [];
        let conversationId = activeConversationId[modelValue];

        if (!conversationId || !conversations[modelValue]?.find(c => c.id === conversationId)) {
            // 创建新对话
            currentConversation = {
                id: `conv-${modelValue}-${timestamp}`,
                title: userPrompt.slice(0, 30) + '...',
                messages: [],
                timestamp
            };
            
            // 更新对话列表和活跃对话ID
            setConversations(prev => ({
                ...prev,
                [modelValue]: [currentConversation, ...(prev[modelValue] || [])]
            }));
            
            setActiveConversationId(prev => ({
                ...prev,
                [modelValue]: currentConversation.id
            }));
            
            conversationId = currentConversation.id;
        } else {
            // 获取现有对话
            currentConversation = conversations[modelValue].find(c => c.id === conversationId);
            currentMessages = [...(currentConversation?.messages || [])];
        }

        // 添加用户消息
        const userMessage = {
            role: 'user',
            content: userPrompt,
            timestamp
        };
        
        const updatedMessages = [...currentMessages, userMessage];
        
        // 立即更新对话显示
        setConversations(prev => {
            const updatedConversations = {
                ...prev,
                [modelValue]: prev[modelValue]?.map(conv =>
                    conv.id === conversationId
                        ? {...conv, messages: updatedMessages}
                        : conv
                ) || []
            };
            // 同步到 localStorage
            localStorage.setItem('conversations', JSON.stringify(updatedConversations));
            return updatedConversations;
        });

        // 处理助手回复
        let responseContent = '';
        const controller = new AbortController();
        controllerRef.current = controller;

        try {
            await sendRequest(
                model.value,
                userPrompt,
                (chunk) => {
                    responseContent += chunk;
                    // 实时更新助手回复
                    setConversations(prev => {
                        const updatedConversations = {
                            ...prev,
                            [modelValue]: prev[modelValue]?.map(conv =>
                                conv.id === conversationId
                                    ? {
                                        ...conv,
                                        messages: [
                                            ...updatedMessages,
                                            {
                                                role: 'assistant',
                                                content: responseContent,
                                                timestamp: Date.now()
                                            }
                                        ]
                                    }
                                    : conv
                            )
                        };
                        // 同步到 localStorage
                        localStorage.setItem('conversations', JSON.stringify(updatedConversations));
                        return updatedConversations;
                    });
                },
                api.api_base,
                api.api_key,
                controller.signal,
                currentMessages
            );
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Request aborted');
            } else {
                console.error('Error in dialog mode:', error);
            }
        }
    };

    const handleParallelMode = async (modelValues, userPrompt, timestamp) => {
        // 清除所有输出
        setOutputs({});
        
        const controller = new AbortController();
        controllerRef.current = controller;

        // 并行处理所有模型的请求
        try {
            await Promise.all(modelValues.map(async (modelValue) => {
                const model = models.find((m) => m.value === modelValue);
                const {apiName} = model;
                const api = apiConfig[apiName];
                
                let content = '';
                try {
                    await sendRequest(
                        model.value,
                        userPrompt,
                        (chunk) => {
                            content += chunk;
                            if (!controller.signal.aborted) {
                                // 更新实时输出
                                setOutputs(prev => ({
                                    ...prev,
                                    [modelValue]: content
                                }));
                            }
                        },
                        api.api_base,
                        api.api_key,
                        controller.signal
                    );
                } catch (error) {
                    console.error(`Error with model ${modelValue}:`, error);
                }
            }));
        } catch (error) {
            console.error('Error in parallel mode:', error);
        }
    };

    const handleModelClose = (model) => {
        setSelectedModels((prev) => prev.filter((m) => m !== model));
    };

    const handleRegenerate = async (model) => {
        setOutputs((prev) => ({...prev, [model]: ''}));
        const selectedModel = models.find((m) => m.value === model);
        const {apiName} = selectedModel;
        const api = apiConfig[apiName];

        if (!api) {
            console.error(`API configuration not found for ${apiName}`);
            return;
        }

        try {
            await sendRequest(
                model,
                prompt,
                (chunk) =>
                    setOutputs((prev) => ({
                        ...prev,
                        [model]: (prev[model] || '') + chunk,
                    })),
                api.api_base,
                api.api_key
            );
        } catch (error) {
            console.error(`Error regenerating response for model: ${model}`, error);
        }
    };

    const handleClear = (model) => {
        setOutputs((prev) => ({...prev, [model]: ''}));
    };

    const handleSaveAllOutputs = () => {
        const combinedOutput = selectedModels
            .map((modelVal) => {
                const modelObj = models.find((m) => m.value === modelVal) || {label: modelVal};
                const title = modelObj.label;
                const output = outputs[modelVal] || '';
                return `--------------------块 ${title} --------------------\n${output}`;
            })
            .join('\n\n');

        navigator.clipboard.writeText(combinedOutput).then(() => {
            alert('所有输出已复制到剪贴板！');
        });
    };

    const toggleSettings = () => {
        setIsSettingsOpen((prev) => !prev);
    };

    const handleModeChange = () => {
        setIsChatMode(prev => {
            const newMode = !prev;
            if (newMode) {
                // 切换到对话模式时，确保只选择一个模型
                if (selectedModels.length > 1) {
                    setSelectedModels([selectedModels[0]]);
                } else if (selectedModels.length === 0 && models.length > 0) {
                    // 如果没有选择模型，默认选择第一个
                    setSelectedModels([models[0].value]);
                }
                // 确保历史记录侧边栏可见
                setIsHistoryOpen(true);
                // 确保当前选中的模型有一个活跃的对话ID
                const modelValue = selectedModels[0] || (models.length > 0 ? models[0].value : null);
                if (modelValue && !activeConversationId[modelValue]) {
                    setActiveConversationId(prev => ({
                        ...prev,
                        [modelValue]: conversations[modelValue]?.[0]?.id || null
                    }));
                }
            } else {
                // 切换到并行模式时，关闭历史记录侧边栏
                setIsHistoryOpen(false);
            }
            return newMode;
        });
    };

    const handleCreateNewConversation = () => {
        const modelValue = selectedModels[0];
        if (!modelValue) return;

        const timestamp = new Date().getTime();
        const newConversation = {
            id: `conv-${modelValue}-${timestamp}`,
            title: '新对话',
            messages: [],
            timestamp
        };

        setConversations(prev => ({
            ...prev,
            [modelValue]: [newConversation, ...(prev[modelValue] || [])]
        }));

        setActiveConversationId(prev => ({
            ...prev,
            [modelValue]: newConversation.id
        }));

        // 保存到 localStorage
        const updatedConversations = {
            ...conversations,
            [modelValue]: [newConversation, ...(conversations[modelValue] || [])]
        };
        localStorage.setItem('conversations', JSON.stringify(updatedConversations));

        // 打开历史记录侧边栏
        setIsHistoryOpen(true);
    };

    const handleDeleteConversation = (modelValue, conversationId) => {
        setConversations(prev => {
            const newConversations = { ...prev };
            if (conversationId === null) {
                // 清空当前模型的所有对话
                newConversations[modelValue] = [];
                setActiveConversationId(prev => ({
                    ...prev,
                    [modelValue]: null
                }));
            } else {
                // 删除单个对话
                newConversations[modelValue] = prev[modelValue].filter(conv => conv.id !== conversationId);
                // 如果删除的是当前活跃的对话，将活跃对话设置为剩余对话中的第一个
                if (activeConversationId[modelValue] === conversationId) {
                    setActiveConversationId(prev => ({
                        ...prev,
                        [modelValue]: newConversations[modelValue][0]?.id || null
                    }));
                }
            }
            
            // 保存到 localStorage
            localStorage.setItem('conversations', JSON.stringify(newConversations));
            return newConversations;
        });
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <div className="App">
                <div className="Sidebar">
                    <h2>AI社区</h2>
                    <div className="mode-toggle">
                        <span className="toggle-label">并行</span>
                        <label className="toggle-switch">
                            <input 
                                type="checkbox" 
                                checked={isChatMode}
                                onChange={handleModeChange}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                        <span className="toggle-label">对话</span>
                    </div>
                    <ModelSelector
                        models={models}
                        selectedModels={selectedModels}
                        onSelect={handleModelSelect}
                        disabled={isChatMode}
                    />
                    <button className="SettingsButton" onClick={toggleSettings}>
                        <img src={settingsIcon} alt="Settings"/>
                    </button>
                    <div className="SidebarFooter">版权所有 © zhuowater@gmail.com</div>
                </div>
                <div className="MainContent">
                    {isChatMode ? (
                        <>
                            <ChatModelSelector
                                models={models}
                                selectedModel={selectedModels[0]}
                                onSelect={(modelValue) => setSelectedModels([modelValue])}
                                onNewChat={handleCreateNewConversation}
                            />
                            <ChatWindow
                                messages={conversations[selectedModels[0]]?.find(
                                    conv => conv.id === activeConversationId[selectedModels[0]]
                                )?.messages || []}
                                selectedModel={selectedModels[0]}
                                modelConfig={models.find(m => m.value === selectedModels[0])}
                            />
                        </>
                    ) : (
                        <>
                            <div className="SaveAllButtonContainer">
                                <button className="SaveAllButton" onClick={handleSaveAllOutputs} title="复制所有输出">
                                    📋
                                </button>
                            </div>
                            <div className="ModelOutputs">
                                {selectedModels.map((modelVal, index) => {
                                    const modelObj = models.find((m) => m.value === modelVal) || {
                                        value: modelVal,
                                        label: modelVal,
                                        icon: douIcon,
                                    };
                                    return (
                                        <DraggableModelOutput
                                            key={modelVal}
                                            model={modelVal}
                                            title={modelObj.label}
                                            icon={modelObj.icon}
                                            output={outputs[modelVal] || ''}
                                            index={index}
                                            onClose={handleModelClose}
                                            onRegenerate={handleRegenerate}
                                            onClear={handleClear}
                                            moveModel={moveModel}
                                        />
                                    );
                                })}
                            </div>
                        </>
                    )}
                    {isSettingsOpen && <SettingsModal onClose={toggleSettings}/>}
                    <div className="ChatBox">
                        <textarea
                            value={prompt}
                            onChange={handlePromptChange}
                            placeholder="请输入想说的话或者提示语."
                        />
                        <button onClick={handleSubmit}>{isStreaming ? '暂停' : '发送'}</button>
                    </div>
                </div>
                {isChatMode && (
                    <HistorySidebar
                        isOpen={isHistoryOpen}
                        onToggle={() => setIsHistoryOpen(!isHistoryOpen)}
                        conversations={conversations}
                        onSelectConversation={handleSelectConversation}
                        activeConversationId={activeConversationId}
                        models={config.models}
                        selectedModel={selectedModels[0]}
                        onDeleteConversation={handleDeleteConversation}
                    />
                )}
            </div>
        </DndProvider>
    );
};

export default App;
